import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Tobias Schwarzkopf
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : wiki parts
// =============================================================================
// ---Third-party imports
import React from 'react';
import { Menu, Grid, Segment, Loader, Table, Button, Icon } from 'semantic-ui-react';
// ---Framework imports
import { dict, mapItems, isArray } from 'ldash';
import log from 'uix/logging';
import { Sel, Actions } from 'uix/registries';
import { useMemo, useState, usePersistent, useApi, useActions, usePartContext } from 'uix/hooks';
import { Link, ConfirmButton } from 'uix/components';
import { DomainLabel } from 'uix/domain/label';
import { formatDate } from 'uix/utils';
// ---Local imports
import { useWikiTemplates } from './hooks';
// ---Globals
export const CursorTagInfo = ({ mode, token })=>{
    let wikiTemplates = useWikiTemplates();
    let byTag = useMemo(()=>dict(mapItems(wikiTemplates, (name, rule)=>[
                rule.tagMatcher,
                {
                    ...rule,
                    name
                }
            ], // only rules created by GenericTagHandler
        (name, rule)=>rule.tagMatcher)), [
        wikiTemplates
    ]);
    let toktype = token?.type;
    let tagName = null;
    let attrName = null;
    if (toktype == 'tag') {
        tagName = token.string;
    } else if (toktype == 'attribute') {
        tagName = token.state.tagName;
        attrName = token.string;
    }
    if (tagName) {
        let rule = byTag[tagName];
        let fmt_attr = '';
        if (attrName) {
            let values = '...';
            if (rule) {
                let compl = rule.completion && rule.completion();
                if (compl) {
                    let vs = compl[tagName]?.attrs[attrName];
                    values = isArray(vs) ? vs.slice(0, 3).join(',') : '?';
                }
            }
            fmt_attr = ` ${attrName}=${values}`;
        }
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx("div", {
                    children: `<${tagName}${fmt_attr}>`
                }),
                rule?.docs && /*#__PURE__*/ _jsx("div", {
                    children: /*#__PURE__*/ _jsx("i", {
                        children: rule.docs
                    })
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                children: /*#__PURE__*/ _jsx("tt", {
                    children: mode.name
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                children: token?.type || null
            })
        ]
    });
};
export const ChainInfo = ({ identifier, changedTs })=>{
    let { data } = useApi('getWikiChain', {
        identifier
    }, {
        autoreset: false,
        deps: changedTs
    });
    let deleteTreeRecords = useActions(Actions.deleteTreeRecords);
    let { reload } = usePartContext();
    if (!data) {
        return /*#__PURE__*/ _jsx(Loader, {
            inline: true,
            active: true,
            size: "mini"
        });
    }
    let build = (source, item)=>{
        if (!item) {
            return;
        }
        let { fspath } = item;
        let label = fspath && fspath.startsWith('https://help.vls.io') ? fspath.slice(19) : fspath;
        let buttons = null;
        if (item.otid) {
            label = /*#__PURE__*/ _jsx(Link, {
                dname: source,
                classname: "xwiki",
                tabname: "edit",
                children: item.caption || item.otid
            });
            buttons = /*#__PURE__*/ _jsx(ConfirmButton, {
                compact: true,
                icon: "trash",
                onClick: ()=>deleteTreeRecords({
                        otid: item.otid,
                        erase: true,
                        onDeleted: ()=>reload()
                    }),
                confirm: `Delete wiki record ${item.otid}`
            });
        }
        let formatSource = (source)=>({
                _hub_: 'help.vls.io',
                _core_: 'core'
            })[source] || source;
        return /*#__PURE__*/ _jsxs(Table.Row, {
            children: [
                /*#__PURE__*/ _jsx(Table.Cell, {
                    children: item.otid ? /*#__PURE__*/ _jsx(DomainLabel, {
                        shortform: true,
                        dname: source
                    }) : formatSource(source)
                }),
                /*#__PURE__*/ _jsx(Table.Cell, {
                    children: label
                }),
                /*#__PURE__*/ _jsx(Table.Cell, {
                    children: item.mimetype
                }),
                /*#__PURE__*/ _jsx(Table.Cell, {
                    children: formatDate(item.ts)
                }),
                /*#__PURE__*/ _jsx(Table.Cell, {
                    children: buttons
                })
            ]
        });
    };
    let rows = [
        ...data.dnames,
        '_core_',
        '_hub_'
    ].filter((k)=>data.wikiChain[k]?.db || data.wikiChain[k]?.fs).map((k)=>{
        let items = data.wikiChain[k];
        return /*#__PURE__*/ _jsxs(React.Fragment, {
            children: [
                build(k, items.db),
                build(k, items.fs)
            ]
        }, k);
    });
    if (rows.length == 0) {
        return null;
    }
    return /*#__PURE__*/ _jsx(Table, {
        compact: true,
        singleLine: true,
        children: /*#__PURE__*/ _jsx(Table.Body, {
            children: rows
        })
    });
};
export const WikiInfoPane = ({ identifier, changedTs, cmToken, cmMode })=>{
    let [active, setActive] = usePersistent('activeWikiInfoPane', 'taginfo');
    let content = null;
    //~ return <Tab menu={{fluid: true, vertical: true, tabular: 'right'}} panes={panes} />
    if (active == 'chain' && identifier) {
        content = /*#__PURE__*/ _jsx(ChainInfo, {
            changedTs: changedTs,
            identifier: identifier
        });
    } else {
        content = /*#__PURE__*/ _jsx(CursorTagInfo, {
            mode: cmMode,
            token: cmToken
        });
    }
    return /*#__PURE__*/ _jsxs(Grid, {
        children: [
            /*#__PURE__*/ _jsx(Grid.Column, {
                stretched: true,
                width: 13,
                children: content
            }),
            /*#__PURE__*/ _jsx(Grid.Column, {
                width: 3,
                children: /*#__PURE__*/ _jsxs(Menu, {
                    vertical: true,
                    fluid: true,
                    borderless: true,
                    children: [
                        /*#__PURE__*/ _jsx(Menu.Item, {
                            name: "info",
                            active: active === 'taginfo',
                            onClick: ()=>setActive('taginfo')
                        }),
                        identifier && /*#__PURE__*/ _jsx(Menu.Item, {
                            name: "chain",
                            active: active === 'chain',
                            onClick: ()=>setActive('chain')
                        })
                    ]
                })
            })
        ]
    });
};
